/**
 * EmployerRoleWizardPage.jsx — Employer v1.1 Phase 0.6 Slice B.
 *
 * Orchestrates the draft authoring wizard. Owns:
 *   - URL <-> step sync (?step=1..3)
 *   - useRoleDraft hook (load / save / dirty / staleTab)
 *   - useDebouncedAutosave wiring (1500 ms) per step
 *   - "first save" flow when on /employer/roles/new (creates the row,
 *     then replaces the URL to /employer/roles/{id}/edit?step=N)
 *
 * Step 4 is rendered as a locked tab by the layout component until
 * Slice C ships the publish flow.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { useDebouncedAutosave } from '@/lib/employer/useDebouncedAutosave';
import { useRoleDraft }         from '@/lib/employer/useRoleDraft';
import { publishRole }          from '@/lib/employer/rolesApi';
import RoleWizardLayout, { WIZARD_STEPS } from '@/components/employer/roles/RoleWizardLayout';
import RoleWizardStepBasics           from '@/components/employer/roles/RoleWizardStepBasics';
import RoleWizardStepResponsibilities from '@/components/employer/roles/RoleWizardStepResponsibilities';
import RoleWizardStepCriteria         from '@/components/employer/roles/RoleWizardStepCriteria';
import RoleWizardStepReview           from '@/components/employer/roles/RoleWizardStepReview';
import PublishConfirmationModal      from '@/components/employer/roles/PublishConfirmationModal';

function parseStep(raw) {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return 1;
  // Phase 0.6 Slice C — Step 4 (Review & publish) is now allowed.
  return Math.max(1, Math.min(4, n));
}

export default function EmployerRoleWizardPage() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const [params, setParams] = useSearchParams();
  const step         = parseStep(params.get('step'));

  const draft        = useRoleDraft({ roleId: id || null });
  const {
    id: roleId, status, loading, loadError,
    title, setTitle,
    definition, setDefinition,
    criteria,   setCriteria,
    dirtyDefinition, dirtyCriteria,
    saveBasicsResponsibilities, saveCriteria,
    staleTab, reloadFromServer,
  } = draft;

  // Persist last-known id so we can navigate after the first save.
  const lastIdRef = useRef(roleId || null);
  useEffect(() => { if (roleId) lastIdRef.current = roleId; }, [roleId]);

  // ─── Save functions for the debouncer ──────────────────────
  const saveDefn = useCallback(async () => {
    const wasNew  = !id;
    const result  = await saveBasicsResponsibilities({ title, definition });
    // First save promotes /new → /{id}/edit. The hook's setId fires
    // asynchronously so we extract the id from the returned row.
    if (wasNew) {
      const newId = (result && result.id) || lastIdRef.current;
      if (newId) {
        navigate(`/employer/roles/${newId}/edit?step=${step}`, { replace: true });
      }
    }
    return result;
  }, [saveBasicsResponsibilities, title, definition, id, navigate, step]);

  const saveCrit = useCallback(async () => {
    return saveCriteria(criteria);
  }, [saveCriteria, criteria]);

  const defnAutosave = useDebouncedAutosave(saveDefn);
  const critAutosave = useDebouncedAutosave(saveCrit);

  // ─── Pending save effects per step ─────────────────────────
  const isStep3   = step === 3;
  const dDef      = dirtyDefinition();
  const dCrit     = dirtyCriteria();

  useEffect(() => {
    if (isStep3) return;
    if (dDef) defnAutosave.trigger({ at: Date.now() });
  // dDef changes when title/definition change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, definition, isStep3]);

  useEffect(() => {
    if (!isStep3) return;
    // Step 3 autosave only fires once the row exists.
    if (!roleId) return;
    if (dCrit) critAutosave.trigger({ at: Date.now() });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria, isStep3, roleId]);

  // Flush any pending save when leaving Step 1/2 → Step 3 etc.
  const onStepChange = useCallback(async (nextN) => {
    try {
      if (step !== 3 && dDef) await defnAutosave.flush();
      if (step === 3 && dCrit) await critAutosave.flush();
    } catch (e) {
      toast.error(e?.message || 'Could not save before navigating');
      return;
    }
    setParams({ step: String(nextN) }, { replace: false });
  }, [step, dDef, dCrit, defnAutosave, critAutosave, setParams]);

  // Explicit "Save draft" — bypasses debounce, prefers the active step.
  const onSaveDraft = useCallback(async () => {
    try {
      if (step !== 3 && dDef) await defnAutosave.flush();
      if (step === 3 && dCrit) await critAutosave.flush();
      toast.success('Draft saved');
    } catch (e) {
      toast.error(e?.message || 'Save failed');
    }
  }, [step, dDef, dCrit, defnAutosave, critAutosave]);

  // ─── Per-step save indicator selection ─────────────────────
  const stepAutosave = isStep3 ? critAutosave : defnAutosave;
  const dirty        = isStep3 ? dCrit : (dDef || (step === 1 && !roleId && Boolean((title || '').trim())));

  const stepKey = useMemo(() => {
    const found = WIZARD_STEPS.find((s) => s.n === step);
    return found ? found.key : 'basics';
  }, [step]);

  // ─── Slice C — Publish dispatch ────────────────────────────
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishPending,   setPublishPending]   = useState(false);
  const [publishError,     setPublishError]     = useState(null);

  const openPublishModal = useCallback(async () => {
    // Flush any pending autosave so publish runs against the latest
    // server state (W-4 mitigation).
    try {
      if (dDef)  await defnAutosave.flush();
      if (dCrit) await critAutosave.flush();
    } catch (e) {
      toast.error(e?.message || 'Save your draft before publishing');
      return;
    }
    setPublishError(null);
    setPublishModalOpen(true);
  }, [dDef, dCrit, defnAutosave, critAutosave]);

  const confirmPublish = useCallback(async () => {
    if (!roleId) return;
    setPublishPending(true);
    setPublishError(null);
    try {
      const resp = await publishRole(roleId);
      const updatedRole = resp?.role || null;
      if (resp?.plan_unactivated) {
        toast.success(
          'Role published. Heads up: your plan isn\u2019t fully activated yet — counter not incremented.',
        );
      } else if (resp?.already_active) {
        toast.message('Role was already active.');
      } else {
        toast.success('Role published.');
      }
      setPublishModalOpen(false);
      // Redirect to the read-only role detail surface (§2.6).
      navigate(
        `/employer/roles/${(updatedRole && updatedRole.id) || roleId}`,
        { replace: true },
      );
    } catch (e) {
      // Backend `detail` may be string (400 assert_publishable) or
      // object (P0001 RPC mapping). Surface verbatim per §2.10.
      const d = e?.detail ?? e?.message ?? 'Publish failed';
      let msg;
      if (typeof d === 'string') {
        msg = d;
      } else if (d && typeof d === 'object') {
        msg = d.detail || d.message || d.code || 'Publish failed';
      } else {
        msg = 'Publish failed';
      }
      setPublishError(msg);
    } finally {
      setPublishPending(false);
    }
  }, [roleId, navigate]);

  const cancelPublish = useCallback(() => {
    if (publishPending) return;
    setPublishModalOpen(false);
    setPublishError(null);
  }, [publishPending]);

  // ─── Loading + error states ────────────────────────────────
  if (loading) {
    return (
      <div
        className="flex items-center gap-2 p-8 text-sm text-slate-300"
        data-testid="wizard-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading wizard…
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="p-6 text-sm text-rose-200" data-testid="wizard-load-error">
        {loadError}
      </div>
    );
  }

  return (
    <RoleWizardLayout
      step={step}
      onStepChange={onStepChange}
      roleId={roleId}
      roleTitle={title}
      status={status}
      saveStatus={stepAutosave.status}
      saveError={stepAutosave.error}
      lastSavedAt={stepAutosave.lastSavedAt}
      dirty={dirty}
      onSaveDraft={onSaveDraft}
      staleTab={staleTab}
      onReloadStale={reloadFromServer}
    >
      <div data-testid={`wizard-active-step-${stepKey}`}>
        {step === 1 ? (
          <RoleWizardStepBasics
            title={title}
            definition={definition}
            onTitleChange={setTitle}
            onDefinitionChange={setDefinition}
          />
        ) : null}
        {step === 2 ? (
          <RoleWizardStepResponsibilities
            definition={definition}
            onDefinitionChange={setDefinition}
          />
        ) : null}
        {step === 3 ? (
          <RoleWizardStepCriteria
            roleId={roleId}
            criteria={criteria}
            onCriteriaChange={setCriteria}
          />
        ) : null}
        {step === 4 ? (
          <RoleWizardStepReview
            roleId={roleId}
            title={title}
            definition={definition}
            criteria={criteria}
            onJumpToStep={(n) => setParams({ step: String(n) }, { replace: false })}
            onPublish={openPublishModal}
          />
        ) : null}
      </div>
      <PublishConfirmationModal
        open={publishModalOpen}
        onCancel={cancelPublish}
        onConfirm={confirmPublish}
        pending={publishPending}
        error={publishError}
      />
    </RoleWizardLayout>
  );
}
